import Papa from 'papaparse';
import type { Curve, ParsedData } from '../../../../interfaces/toolData';
import type { WaveFileType } from '../../types';
import { cleanTimeSeriesData } from '../../../dataQuality';

/**
 * Parser for JAE Smart DM SURVEY files
 * 
 * Differences from MicroPulse SURVEY:
 * - Different header signature (SmartDM vs MicroPulse)
 * - Different column structure (Battery 2, no duplicate gravity sensors)
 * - Slightly different metadata format
 */
export class SmartDmSurveyParser {
  static parse(fileContent: string): ParsedData {
    const lines = fileContent.split('\n');
    
    // Validate file format
    if (!lines[0]?.trim().startsWith('SmartDM')) {
      throw new Error('Invalid JAE Smart DM SURVEY file format');
    }
    
    if (!lines[1]?.trim().includes('SURVEY Log')) {
      throw new Error('Not a JAE Smart DM SURVEY file');
    }
    
    // Extract metadata from header
    const metadata = this.extractMetadata(lines);
    
    // Find the data header and data start
    let dataHeaderIndex = -1;
    let dataStartIndex = -1;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line.includes('Time, Inclination, Azimuth')) {
        dataHeaderIndex = i;
        // Skip both the header line and the units line
        dataStartIndex = i + 2;
        break;
      }
    }
    
    if (dataHeaderIndex === -1) {
      throw new Error('Could not find data header in JAE Smart DM SURVEY file');
    }
    
    // Get column names from the header line
    const headerLine = lines[dataHeaderIndex];
    const columnNames = headerLine.split(',').map(name => name.trim());
    
    // Parse CSV data starting from the actual data rows
    const csvData = lines.slice(dataStartIndex).join('\n');
    const parseResult = Papa.parse(csvData, {
      header: false, // Don't use header since we extracted it manually
      skipEmptyLines: true,
      transform: (value: string) => value.trim()
    });
    
    if (parseResult.errors.length > 0) {
      console.warn('CSV parsing errors:', parseResult.errors);
    }
    
    const rawData = parseResult.data as string[][];
    
    if (rawData.length === 0) {
      throw new Error('No valid data found in JAE Smart DM SURVEY file');
    }
    
    // Convert array data to objects using extracted column names
    const data: Record<string, string>[] = rawData.map(row => {
      const record: Record<string, string> = {};
      columnNames.forEach((colName, index) => {
        if (index < row.length) {
          record[colName] = row[index];
        }
      });
      return record;
    });
    
    // Generate curves
    const curves = this.generateCurves(data);
    
    return {
      toolType: 'JAE',
      fileType: 'JAE_SMARTDM_SURVEY' as WaveFileType,
      curves,
      metadata,
      dataPreview: {
        totalCount: data.length,
        first5: data.slice(0, 5)
      }
    };
  }
  
  private static extractMetadata(lines: string[]): Record<string, string> {
    const metadata: Record<string, string> = {};
    
    for (const line of lines) {
      const trimmed = line.trim();
      
      // Extract key-value pairs from header
      if (trimmed.includes(':') && !trimmed.includes('Time,')) {
        const [key, ...valueParts] = trimmed.split(':');
        const value = valueParts.join(':').trim();
        
        if (key && value) {
          metadata[key.trim()] = value;
        }
      }
    }
    
    return metadata;
  }
  
  private static generateCurves(data: Record<string, string>[]): Curve[] {
    const curves: Curve[] = [];
    
    // Parse timestamps
    const timestamps: Date[] = [];
    const timeColumn = 'Time';
    
    for (const row of data) {
      const timeStr = row[timeColumn];
      if (timeStr) {
        const date = new Date(timeStr);
        if (!isNaN(date.getTime())) {
          timestamps.push(date);
        }
      }
    }
    
    if (timestamps.length === 0) {
      throw new Error('No valid timestamp data found in JAE Smart DM SURVEY file');
    }
    
    // Define the columns we want to extract as curves
    const curveColumns = [
      { name: 'Inclination', unit: 'degrees' },
      { name: 'Azimuth', unit: 'degrees' },
      { name: 'Gravity Tool-Face', unit: 'degrees' },
      { name: 'Magnetic Tool-Face', unit: 'degrees' },
      { name: 'Dip Angle', unit: 'degrees' },
      { name: 'Temperature', unit: 'celsius' },
      { name: 'MagF', unit: 'gauss' },
      { name: 'Total Grav', unit: 'g' },
      { name: 'GX', unit: 'g' },
      { name: 'GY', unit: 'g' },
      { name: 'GZ', unit: 'g' },
      { name: 'MX', unit: 'gauss' },
      { name: 'MY', unit: 'gauss' },
      { name: 'MZ', unit: 'gauss' },
      { name: 'Battery', unit: 'v' },
      { name: 'Battery 2', unit: 'on/off' }
    ];
    
    for (const column of curveColumns) {
      const yValues: number[] = [];
      const validTimes: Date[] = [];
      
      for (let i = 0; i < data.length; i++) {
        const valueStr = data[i][column.name];
        const time = timestamps[i];
        
        if (valueStr && time) {
          const value = parseFloat(valueStr);
          if (!isNaN(value)) {
            yValues.push(value);
            validTimes.push(time);
          }
        }
      }
      
      if (yValues.length > 0) {
        const rawCurve: Curve = {
          name: column.name,
          xValues: validTimes,
          yValues,
          yUnit: column.unit
        };
        
        // Clean the data
        const cleanupResult = cleanTimeSeriesData(rawCurve);
        if (cleanupResult.cleanedCurve.xValues.length > 0) {
          curves.push(cleanupResult.cleanedCurve);
        }
      }
    }
    
    return curves;
  }
} 